import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios'
import { ErrorHandler } from '../utils/errorHandler'
import { OfflineQueue, RetryManager } from '../utils/offlineQueue'
import { getApiBaseUrl } from '../config/runtime-config'

// Create axios instance with runtime-evaluated base URL
const apiClient: AxiosInstance = axios.create({
  baseURL: getApiBaseUrl(),
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token')
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`
    }
    
    // Add request ID for tracking
    config.headers['X-Request-ID'] = crypto.randomUUID()
    
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => {
    // Handle successful response
    return response
  },
  async (error: AxiosError) => {
    // Handle network errors
    if (!error.response) {
      if (!navigator.onLine) {
        // Check if this is a queueable request
        const config = error.config
        if (config && isQueueableRequest(config)) {
          // Add to offline queue
          const queueId = OfflineQueue.enqueue({
            url: config.url || '',
            method: config.method || 'GET',
            data: config.data,
            headers: config.headers as Record<string, string>
          })
          
          // Return a mock response for offline-capable operations
          return {
            data: {
              offline: true,
              queueId,
              message: 'Request queued for offline processing'
            },
            status: 202,
            statusText: 'Accepted Offline',
            headers: {},
            config
          }
        }
        
        ErrorHandler.handleApiError({
          name: 'NetworkError',
          message: 'No internet connection',
          response: { data: { error: { code: 'NETWORK_ERROR' } } }
        })
      } else {
        ErrorHandler.handleApiError(error)
      }
      return Promise.reject(error)
    }

    // Handle 401 - Unauthorized
    if (error.response.status === 401) {
      // Clear auth data
      localStorage.removeItem('auth_token')
      localStorage.removeItem('user')
      
      // Only redirect if not already on login page
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login'
      }
    }

    // Handle other errors
    ErrorHandler.handleApiError(error)
    
    return Promise.reject(error)
  }
)

// Check if request can be queued for offline processing
function isQueueableRequest(config: AxiosRequestConfig): boolean {
  const method = config.method?.toUpperCase()
  const url = config.url || ''
  
  // Queue POST requests for certain endpoints
  const queueableEndpoints = [
    '/api/chat/feedback',
    '/api/feedback',
    '/api/memory/preferences',
    '/api/errors/log'
  ]
  
  return (
    method === 'POST' &&
    queueableEndpoints.some(endpoint => url.includes(endpoint))
  )
}

// API methods with TypeScript support
export const api = {
  // Auth endpoints
  auth: {
    login: async (token: string) => {
      const response = await apiClient.post('/auth/google', { token })
      return response.data
    },
    
    logout: async () => {
      const response = await apiClient.post('/auth/logout')
      return response.data
    },
    
    me: async () => {
      const response = await apiClient.get('/auth/me')
      return response.data
    },
  },

  // Search endpoints
  search: {
    query: async (params: {
      query: string
      limit?: number
      search_type?: string
      filters?: Record<string, any>
    }) => {
      const response = await apiClient.post('/api/search', params)
      return response.data
    },
    
    suggestions: async (query: string) => {
      const response = await apiClient.get('/api/search/suggestions', {
        params: { q: query }
      })
      return response.data
    },
    
    popular: async (limit: number = 10) => {
      const response = await apiClient.get('/api/search/popular', {
        params: { limit }
      })
      return response.data
    },
  },

  // Chat endpoints
  chat: {
    send: async (params: {
      messages: Array<{ role: string; content: string }>
      conversation_id?: string
      session_id?: string
      use_memory?: boolean
      model_override?: string
    }) => {
      // Use retry for chat requests
      return RetryManager.withRetry(
        async () => {
          const response = await apiClient.post('/api/chat', params)
          return response.data
        },
        {
          maxRetries: 2,
          retryIf: (error) => {
            // Retry on network errors or 5xx errors
            return !error.response || error.response.status >= 500
          },
          onRetry: (attempt, delay) => {
            ErrorHandler.showInfo(`Verbindungsversuch ${attempt}... (${delay}ms)`)
          }
        }
      )
    },
    
    history: async (conversationId?: string, limit: number = 50) => {
      const response = await apiClient.get('/api/chat/history', {
        params: { conversation_id: conversationId, limit }
      })
      return response.data
    },
    
    feedback: async (conversationId: string, feedback: number) => {
      const response = await apiClient.post('/api/chat/feedback', {
        conversation_id: conversationId,
        feedback
      })
      return response.data
    },
  },

  // Document endpoints
  documents: {
    upload: async (files: File[], title?: string, metadata?: any) => {
      const formData = new FormData()
      files.forEach(file => formData.append('files', file))
      if (title) formData.append('title', title)
      if (metadata) formData.append('metadata', JSON.stringify(metadata))
      
      const response = await apiClient.post('/api/ingest/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
            // You can use this to show upload progress
            console.log(`Upload progress: ${progress}%`)
          }
        }
      })
      return response.data
    },
    
    uploadText: async (title: string, content: string, metadata?: any) => {
      const formData = new FormData()
      formData.append('title', title)
      formData.append('content', content)
      if (metadata) formData.append('metadata', JSON.stringify(metadata))
      
      const response = await apiClient.post('/api/ingest/text', formData)
      return response.data
    },
    
    uploadVoice: async (audioFile: File, title?: string, language: string = 'de') => {
      const formData = new FormData()
      formData.append('audio_file', audioFile)
      if (title) formData.append('title', title)
      formData.append('language', language)
      
      const response = await apiClient.post('/api/ingest/voice', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      return response.data
    },
    
    status: async (documentId: string) => {
      const response = await apiClient.get(`/api/ingest/status/${documentId}`)
      return response.data
    },
    
    delete: async (documentId: string) => {
      const response = await apiClient.delete(`/api/ingest/${documentId}`)
      return response.data
    },
  },

  // Memory endpoints
  memory: {
    get: async () => {
      const response = await apiClient.get('/api/memory')
      return response.data
    },
    
    update: async (preferences: any) => {
      const response = await apiClient.put('/api/memory/preferences', preferences)
      return response.data
    },
    
    clear: async () => {
      const response = await apiClient.delete('/api/memory')
      return response.data
    },
  },

  // Feedback endpoints
  feedback: {
    submit: async (feedback: {
      type: string
      message: string
      metadata?: any
    }) => {
      const response = await apiClient.post('/api/feedback', feedback)
      return response.data
    },
  },

  // Admin endpoints
  admin: {
    cacheWarm: async () => {
      const response = await apiClient.post('/api/admin/cache/warm')
      return response.data
    },
    
    cacheClear: async (namespace: string) => {
      const response = await apiClient.post(`/api/admin/cache/clear/${namespace}`)
      return response.data
    },
    
    cacheStats: async () => {
      const response = await apiClient.get('/api/admin/cache/stats')
      return response.data
    },
    
    cacheMetrics: async () => {
      const response = await apiClient.get('/api/admin/cache/metrics')
      return response.data
    },
    
    cacheReport: async (format: string = 'json') => {
      const response = await apiClient.get('/api/admin/cache/report', {
        params: { format }
      })
      return response.data
    },
    
    systemHealth: async () => {
      const response = await apiClient.get('/api/admin/system/health')
      return response.data
    },
    
    // Analytics endpoints
    analyticsOverview: async (days: number = 7) => {
      const response = await apiClient.get('/api/analytics/overview', {
        params: { days }
      })
      return response.data
    },
    
    analyticsRealtime: async () => {
      const response = await apiClient.get('/api/analytics/realtime')
      return response.data
    },
    
    analyticsSearch: async (days: number = 30) => {
      const response = await apiClient.get('/api/analytics/search', {
        params: { days }
      })
      return response.data
    },
    
    analyticsDocuments: async (days: number = 30) => {
      const response = await apiClient.get('/api/analytics/documents', {
        params: { days }
      })
      return response.data
    },
    
    analyticsPerformance: async (hours: number = 24) => {
      const response = await apiClient.get('/api/analytics/performance', {
        params: { hours }
      })
      return response.data
    },
    
    analyticsUser: async (userId: string, days: number = 30) => {
      const response = await apiClient.get(`/api/analytics/users/${userId}`, {
        params: { days }
      })
      return response.data
    },
    
    analyticsExport: async (
      exportType: string,
      format: string = 'json',
      startDate?: Date,
      endDate?: Date
    ) => {
      const response = await apiClient.post('/api/analytics/export', null, {
        params: {
          export_type: exportType,
          format,
          start_date: startDate?.toISOString(),
          end_date: endDate?.toISOString()
        }
      })
      return response.data
    },
  },
}

// Helper function for offline-aware API calls
export async function offlineAwareRequest<T>(
  request: () => Promise<T>,
  options: {
    fallbackValue?: T
    showOfflineMessage?: boolean
    queueIfOffline?: boolean
  } = {}
): Promise<T> {
  try {
    // Check if online before making request
    if (!navigator.onLine && options.fallbackValue !== undefined) {
      if (options.showOfflineMessage) {
        ErrorHandler.showWarning('Sie sind offline. Zeige zwischengespeicherte Daten.')
      }
      return options.fallbackValue
    }
    
    return await request()
  } catch (error) {
    // Handle offline scenario
    if (!navigator.onLine) {
      if (options.fallbackValue !== undefined) {
        return options.fallbackValue
      }
      
      throw new Error('Offline - keine zwischengespeicherten Daten verfügbar')
    }
    
    throw error
  }
}

// Export axios instance for custom requests
export default apiClient